<template>
  <div class="container-fluid">
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>

    <div v-if="isLoading && !review" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <div class="text-muted mt-2">Loading review...</div>
    </div>

    <div v-else-if="storeError && !review" class="alert alert-danger">
      <i class="fa fa-exclamation-circle mr-1"></i> {{ storeError }}
      <b-button variant="outline-danger" size="sm" class="ml-3" @click="loadDetail">
        Retry
      </b-button>
    </div>

    <template v-else-if="review">
      <div class="row clearfix mb-3">
        <div class="col-lg-12">
          <div class="card card-top-line">
            <div class="header d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <h2 class="mb-1">
                  {{ customerFullName }}
                  <span class="badge ml-2" :class="statusBadgeClass(review.status)">
                    {{ statusLabel(review.status) }}
                  </span>
                  <span v-if="review.awaiting_ce" class="badge badge-warning ml-1">
                    Awaiting CE
                  </span>
                </h2>
                <div class="text-muted small">
                  {{ review.customer && review.customer.payangel_id }}
                  · Cycle {{ review.cycle_no }}
                </div>
              </div>
              <div class="mt-2 mt-md-0">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mr-2"
                  @click="goToRemitter"
                >
                  <i class="fa fa-user mr-1"></i> Open remitter
                </b-button>
                <b-button variant="outline-secondary" size="sm" @click="goBack">
                  <i class="fa fa-arrow-left mr-1"></i> Back to queue
                </b-button>
              </div>
            </div>
            <div class="body pt-0">
              <div class="row">
                <div class="col-md-3 mb-2">
                  <small class="text-muted d-block">Email</small>
                  <strong>{{ review.customer && review.customer.email }}</strong>
                </div>
                <div class="col-md-3 mb-2">
                  <small class="text-muted d-block">Phone</small>
                  <strong>{{ review.customer && review.customer.phone }}</strong>
                </div>
                <div class="col-md-3 mb-2">
                  <small class="text-muted d-block">Updated</small>
                  <strong>{{ formatDate(review.updated_at) }}</strong>
                </div>
                <div class="col-md-3 mb-2">
                  <small class="text-muted d-block">Next due</small>
                  <strong>{{ formatDate(review.next_due_at) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row clearfix">
        <div class="col-lg-7">
          <div class="card card-top-line mb-3">
            <div class="header border-bottom">
              <h5 class="text-primary font-weight-normal mb-0">Sections awaiting decision</h5>
            </div>
            <div class="body">
              <div
                v-for="section in sectionRows"
                :key="section.key"
                class="border rounded p-3 mb-3"
              >
                <div class="d-flex justify-content-between align-items-start flex-wrap">
                  <div>
                    <h6 class="mb-1">{{ section.label }}</h6>
                    <span class="badge" :class="sectionBadgeClass(section.status)">
                      {{ sectionStatusLabel(section.status) }}
                    </span>
                  </div>
                  <div
                    v-if="section.status === 'pending_review' && canEdit"
                    class="mt-2 mt-md-0"
                  >
                    <b-button
                      variant="outline-success"
                      size="sm"
                      class="mr-1 mb-1"
                      :disabled="isLoading"
                      @click="confirmDecision(section.key, 'approve')"
                    >
                      <i class="fa fa-check mr-1"></i> Approve
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      class="mr-1 mb-1"
                      :disabled="isLoading"
                      @click="confirmDecision(section.key, 'reject')"
                    >
                      <i class="fa fa-times mr-1"></i> Reject
                    </b-button>
                    <b-button
                      variant="outline-info"
                      size="sm"
                      class="mb-1"
                      :disabled="isLoading"
                      @click="confirmDecision(section.key, 'request_resubmission')"
                    >
                      <i class="fa fa-refresh mr-1"></i> Request resubmission
                    </b-button>
                  </div>
                </div>
                <div class="mt-3 small text-muted">Current values (already saved on customer)</div>
                <dl class="row mb-0 mt-2 section-fields">
                  <template v-for="field in snapshotFields(section.key)">
                    <dt :key="field.key + '-label'" class="col-sm-4 text-muted small mb-1">
                      {{ field.label }}
                    </dt>
                    <dd :key="field.key + '-value'" class="col-sm-8 small mb-1">
                      {{ field.value || "—" }}
                    </dd>
                  </template>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-5">
          <div class="card card-top-line mb-3">
            <div class="header border-bottom">
              <h5 class="text-primary font-weight-normal mb-0">Annual-review POA</h5>
            </div>
            <div class="body">
              <div v-if="!(review.annual_review_documents || []).length" class="text-muted">
                No annual-review proof-of-address documents for this cycle.
              </div>
              <div
                v-for="doc in review.annual_review_documents || []"
                :key="doc.id"
                class="border rounded p-3 mb-2"
              >
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <span class="badge" :class="docBadgeClass(doc.status)">{{ doc.status }}</span>
                  <small class="text-muted">{{ formatDate(doc.updated_at) }}</small>
                </div>
                <div class="mt-2">
                  <a
                    v-if="doc.document_url"
                    :href="doc.document_url"
                    target="_blank"
                    rel="noopener"
                    class="text-primary"
                  >
                    <i class="fa fa-external-link mr-1"></i> Open document
                  </a>
                  <span v-else class="text-muted small">{{ doc.file }}</span>
                </div>
                <div v-if="doc.admin_comment" class="small text-muted mt-1">
                  {{ doc.admin_comment }}
                </div>
                <div v-if="canEdit" class="mt-2">
                  <b-button
                    v-if="doc.status === 'pending' || doc.status === 'resubmission_requested'"
                    variant="outline-success"
                    size="sm"
                    class="mr-1 mb-1"
                    :disabled="documentActionLoading"
                    @click="updatePoaDocument(doc, 'approve')"
                  >
                    Approve
                  </b-button>
                  <b-button
                    v-if="doc.status === 'pending' || doc.status === 'resubmission_requested'"
                    variant="outline-danger"
                    size="sm"
                    class="mr-1 mb-1"
                    :disabled="documentActionLoading"
                    @click="openPoaReasonModal(doc, 'reject')"
                  >
                    Reject
                  </b-button>
                  <b-button
                    v-if="doc.status === 'pending' || doc.status === 'rejected' || doc.status === 'resubmission_requested'"
                    variant="outline-info"
                    size="sm"
                    class="mb-1"
                    :disabled="documentActionLoading"
                    @click="openPoaReasonModal(doc, 'request_resubmission')"
                  >
                    Request resubmission
                  </b-button>
                </div>
              </div>
              <p class="small text-muted mb-0 mt-2">
                Approving / rejecting the Address section also updates pending
                annual POA docs for this cycle. Individual POA actions use the
                same remitter document API as Edit Customer.
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>

    <b-modal
      id="annualReviewPoaReasonModal"
      :title="poaModalTitle"
      @ok="submitPoaReason"
      @hidden="resetPoaModal"
      ok-title="Submit"
      cancel-title="Cancel"
      :ok-disabled="!poaReason.trim() || documentActionLoading"
    >
      <label class="d-block mb-2">Reason <span class="text-danger">*</span></label>
      <b-form-textarea
        v-model="poaReason"
        rows="3"
        placeholder="Please provide a reason..."
        :disabled="documentActionLoading"
      ></b-form-textarea>
    </b-modal>
  </div>
</template>

<script>
import BreadCrumb from "@/components/BreadCrumb.vue";
import { mapGetters } from "vuex";
import { UPDATE_CUSTOMER_DOCUMENT_STATUS } from "@/store/actions.type";

const SECTION_META = [
  { key: "personal_details", label: "Personal details" },
  { key: "home_address", label: "Home address" },
  { key: "contact_details", label: "Contact details" },
  { key: "occupation", label: "Occupation" },
];

const SNAPSHOT_FIELD_LABELS = {
  personal_details: [
    { key: "first_name", label: "First name" },
    { key: "middle_name", label: "Middle name" },
    { key: "last_name", label: "Last name" },
  ],
  home_address: [
    { key: "line1", label: "Address line 1" },
    { key: "line2", label: "Address line 2" },
    { key: "city", label: "City" },
    { key: "state", label: "State / region" },
    { key: "zip", label: "Postcode" },
    { key: "country", label: "Country" },
  ],
  contact_details: [
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
  ],
  occupation: [
    { key: "occupation", label: "Occupation" },
    { key: "employer", label: "Employer" },
  ],
};

export default {
  name: "AnnualReviewDetail",
  components: { BreadCrumb },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Members", links: "/customer/list" },
        { title: "Annual Review", links: "/customer/annual-reviews" },
        { title: "Detail", links: "" },
      ],
      documentActionLoading: false,
      selectedPoaDoc: null,
      poaAction: null,
      poaReason: "",
    };
  },
  computed: {
    ...mapGetters("annualReview", [
      "currentAnnualReview",
      "annualReviewLoading",
      "annualReviewError",
    ]),
    review() {
      return this.currentAnnualReview;
    },
    isLoading() {
      return this.annualReviewLoading;
    },
    storeError() {
      return this.annualReviewError;
    },
    canEdit() {
      return this.$store.getters["authpermission/hasPermission"]("remitter.edit");
    },
    customerFullName() {
      const c = (this.review && this.review.customer) || {};
      return [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ") || "Remitter";
    },
    sectionRows() {
      const sections = (this.review && this.review.sections) || {};
      return SECTION_META.map((meta) => ({
        ...meta,
        status: sections[meta.key] || "pending",
      }));
    },
    reviewId() {
      return this.$route.params.review_id;
    },
    poaModalTitle() {
      if (this.poaAction === "reject") return "Reject POA document";
      if (this.poaAction === "request_resubmission") return "Request POA resubmission";
      return "POA decision";
    },
  },
  watch: {
    reviewId: {
      immediate: true,
      handler(id) {
        if (id) this.loadDetail();
      },
    },
  },
  methods: {
    loadDetail() {
      this.$store.dispatch("annualReview/fetchAnnualReviewDetail", this.reviewId);
    },
    goBack() {
      this.$router.push({ name: "annual-reviews" });
    },
    goToRemitter() {
      const customerId = this.review && this.review.customer && this.review.customer.id;
      if (!customerId) return;
      this.$router.push({ name: "customer-edit", params: { customer_id: customerId } });
    },
    snapshotFields(sectionKey) {
      const snap = (this.review && this.review.snapshot && this.review.snapshot[sectionKey]) || {};
      const labels = SNAPSHOT_FIELD_LABELS[sectionKey] || [];
      return labels.map((field) => ({
        ...field,
        value: snap[field.key],
      }));
    },
    confirmDecision(section, action) {
      const needsReason = action === "reject" || action === "request_resubmission";
      const titles = {
        approve: "Approve section?",
        reject: "Reject section?",
        request_resubmission: "Request resubmission?",
      };
      const texts = {
        approve: `Confirm CE approval for ${section.replace(/_/g, " ")}. Data is already saved on the customer profile.`,
        reject: `Reject ${section.replace(/_/g, " ")} and send it back to pending for the customer.`,
        request_resubmission: `Ask the customer to resubmit ${section.replace(/_/g, " ")}.`,
      };

      const swalOpts = {
        title: titles[action],
        text: texts[action],
        icon: action === "approve" ? "question" : "warning",
        showCancelButton: true,
        confirmButtonText:
          action === "approve"
            ? "Approve"
            : action === "reject"
              ? "Reject"
              : "Request resubmission",
        confirmButtonColor: action === "approve" ? "#4AC9B0" : "#dc3545",
        cancelButtonText: "Cancel",
      };

      if (needsReason) {
        swalOpts.input = "textarea";
        swalOpts.inputPlaceholder = "Reason required...";
        swalOpts.inputValidator = (value) => {
          if (!value || !String(value).trim()) return "A reason is required";
          return null;
        };
      }

      this.$swal(swalOpts).then(async (result) => {
        if (!result.value && !result.isConfirmed) return;
        const admin_comment = needsReason
          ? String(result.value || "").trim()
          : undefined;
        try {
          await this.$store.dispatch("annualReview/decideAnnualReviewSection", {
            reviewId: this.reviewId,
            section,
            action,
            admin_comment,
          });
          this.$bvToast.toast(
            action === "approve" ? "Section approved" : "Section decision saved",
            {
              title: "Annual Review",
              variant: "success",
              solid: true,
              autoHideDelay: 3000,
            },
          );
        } catch (e) {
          this.$bvToast.toast(this.storeError || "Decision failed", {
            title: "Annual Review",
            variant: "danger",
            solid: true,
            autoHideDelay: 5000,
          });
        }
      });
    },
    openPoaReasonModal(doc, action) {
      this.selectedPoaDoc = doc;
      this.poaAction = action;
      this.poaReason = "";
      this.$bvModal.show("annualReviewPoaReasonModal");
    },
    resetPoaModal() {
      this.selectedPoaDoc = null;
      this.poaAction = null;
      this.poaReason = "";
      this.documentActionLoading = false;
    },
    submitPoaReason(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (!this.poaReason.trim() || !this.selectedPoaDoc || !this.poaAction) return;
      this.updatePoaDocument(this.selectedPoaDoc, this.poaAction, this.poaReason.trim());
    },
    async updatePoaDocument(doc, action, adminComment = null) {
      this.documentActionLoading = true;
      try {
        const response = await this.$store.dispatch(UPDATE_CUSTOMER_DOCUMENT_STATUS, {
          documentId: doc.id,
          action,
          admin_comment: adminComment,
        });
        const isSuccess = response && response.status >= 200 && response.status < 300;
        if (!isSuccess) {
          throw new Error(response?.data?.message || "Document update failed");
        }
        this.$bvModal.hide("annualReviewPoaReasonModal");
        this.resetPoaModal();
        await this.loadDetail();
        this.$bvToast.toast("POA document updated", {
          title: "Annual Review",
          variant: "success",
          solid: true,
          autoHideDelay: 3000,
        });
      } catch (e) {
        this.documentActionLoading = false;
        this.$bvToast.toast(e.message || "POA update failed", {
          title: "Annual Review",
          variant: "danger",
          solid: true,
          autoHideDelay: 5000,
        });
      }
    },
    sectionStatusLabel(status) {
      const map = {
        pending: "Pending",
        confirmed: "Confirmed by customer",
        pending_review: "Awaiting CE",
        approved: "CE approved",
      };
      return map[status] || status;
    },
    sectionBadgeClass(status) {
      const map = {
        pending: "badge-secondary",
        confirmed: "badge-success",
        pending_review: "badge-warning",
        approved: "badge-success",
      };
      return map[status] || "badge-secondary";
    },
    statusLabel(status) {
      const map = {
        pending: "Pending",
        in_progress: "In progress",
        completed: "Completed",
      };
      return map[status] || status;
    },
    statusBadgeClass(status) {
      const map = {
        pending: "badge-secondary",
        in_progress: "badge-primary",
        completed: "badge-success",
      };
      return map[status] || "badge-secondary";
    },
    docBadgeClass(status) {
      const map = {
        pending: "badge-warning",
        approved: "badge-success",
        rejected: "badge-danger",
        resubmission_requested: "badge-info",
      };
      return map[status] || "badge-secondary";
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleString();
      } catch (e) {
        return value;
      }
    },
  },
};
</script>

<style scoped>
.section-fields dt,
.section-fields dd {
  word-break: break-word;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
